import React, { useState } from "react";
import { Badge, Button, Col, Form, Row, Table } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { useAppApi } from "@/App";
import { useChoiceLabel } from "@/const";
import { useRequest } from "@components/AppHook";
import { ExportButton } from "@components/Common/Button";
import { DevForm } from "@components/Forms/Fields";
import { useSubmit } from "@components/Forms/Handlers";
import { RenderForm } from "@components/Forms/Render";
import { Awesome, DevModal } from "@components/Helpers";
import { CommonCard, ListingCard } from "@components/Pages";
import { url_reverse } from "@components/Utils";

export default function Playtest(props) {
    const params = useParams();
    if (["users_add", "users_del"].includes(params.act)) {
        return <PlaytestUsersManage {...props} act={params.act} />;
    }
    return <PlaytestUserList {...props} />;
}

function PlaytestUserList({ gameId, header }) {
    const { t } = useTranslation();
    const [showSettings, setShowSettings] = useState(false);
    const [accessGrant, setAccessGrant] = useState({ cnt: 250, show: false });
    const { apiData, apiUrl, setApiReget } = useAppApi(url_reverse("api:game:playtest:users", { game_id: gameId }));
    const { api_post } = useRequest();
    const onFormSubmit = useSubmit();
    const GamePlaytestUserReqState = useChoiceLabel("GamePlaytestUserReqState");

    if (!apiData) return;

    const closeSettings = () => {
        setShowSettings(false);
    };
    const closeAccessGrant = () => {
        setAccessGrant((prev) => ({ ...prev, show: false }));
    };

    const changeReqState = (playtest_user_id, req_state) => {
        api_post(null, apiUrl, {
            params: { action: "change_req_state", playtest_user_id, req_state },
            then: (resp) => {
                if (resp.status === "ok") {
                    setApiReget(true);
                }
            },
        });
    };

    const doAccessGrant = (ev) => {
        api_post(ev, apiUrl, {
            params: { action: "grant_access", cnt: accessGrant.cnt },
            then: (resp) => {
                if (resp.status === "ok") {
                    closeAccessGrant();
                    setApiReget(true);
                }
            },
        });
    };

    const headerTools = (
        <>
            <Button href="#" variant="dark" size="sm" onClick={() => setShowSettings(true)}>
                <Awesome type="cog" /> {t("Настройки")}
            </Button>{" "}
            <Link to={url_reverse("games:playtest:users_del", { game_id: gameId })} className="btn btn-sm btn-dark">
                <Awesome type="minus-circle" /> {t("Удалить")}
            </Link>{" "}
            <Link to={url_reverse("games:playtest:users_add", { game_id: gameId })} className="btn btn-sm btn-dark">
                <Awesome type="plus-circle" /> {t("Добавить")}
            </Link>{" "}
            <ExportButton size="sm" />
        </>
    );

    return (
        <ListingCard header={header} headerTools={headerTools}>
            <DevModal
                size="xl"
                show={showSettings}
                onHide={closeSettings}
                header={t("Настройки")}
                body={<RenderForm prefix="settings" bareForm={true} />}
                footer={
                    <DevForm.Button variant="primary" type="submit" name="action" value="settings">
                        {t("Сохранить")}
                    </DevForm.Button>
                }
                renderWrapper={(children) => (
                    <Form onSubmit={(ev) => onFormSubmit(ev, { onOK: () => closeSettings() })}>{children}</Form>
                )}
            />
            <DevModal
                show={accessGrant.show}
                onHide={closeAccessGrant}
                header={t("Выдать доступ тестерам?")}
                body={t(
                    "Доступ будет выдан N пользователям из очереди ожидания в порядке сортировки по дате запроса (от более ранних к более поздним)."
                )}
                footer={
                    <DevForm.Button variant="primary" name="action" value="access_grant" onClick={doAccessGrant}>
                        {t("Выдать доступ")}
                    </DevForm.Button>
                }
            />

            <div className="mb-3">
                <Badge variant="primary">
                    {t("Всего заявок")}: {apiData.pager.count}
                </Badge>{" "}
                <Badge variant="success">
                    {t("Доступ выдан")}: {apiData.access_cnt}
                </Badge>{" "}
                <Badge variant="warning">
                    {t("В ожидании")}: {apiData.pending_cnt}
                </Badge>
            </div>

            <Row style={{ backgroundColor: "#f4f4f4", padding: "10px" }}>
                <span style={{ width: "250px", marginLeft: "50px" }}>
                    <b>{t("Выдать доступ из очереди")}</b>
                </span>
                <span style={{ width: "170px" }}>
                    <DevForm.TextInput
                        style={{ width: "150px" }}
                        size="sm"
                        value={accessGrant.cnt}
                        onChange={(ev) => {
                            setAccessGrant((prev) => ({ ...prev, cnt: ev.target.value }));
                        }}
                    />
                </span>
                <span>
                    <DevForm.Button
                        size="sm"
                        variant="primary"
                        name="access_grant"
                        onClick={() => setAccessGrant((prev) => ({ ...prev, show: true }))}
                        disabled={!accessGrant.cnt || accessGrant.cnt < 1 || !/^\d+$/.test(accessGrant.cnt)}
                    >
                        {t("Выдать доступ")}
                    </DevForm.Button>
                </span>
            </Row>

            <Table hover responsive className="dmg-table">
                <thead>
                    <tr>
                        <th>Email / ID</th>
                        <th>{t("Статус заявки")}</th>
                        <th>{t("Тип доступа")}</th>
                        <th>{t("Дата запроса")}</th>
                        <th>{t("Дата выдачи доступа")}</th>
                        <th>{t("Комментарий")}</th>
                    </tr>
                </thead>
                <tbody>
                    {apiData.object_list?.map((o) => {
                        return (
                            <tr key={`object_list${o.id}`}>
                                <td>{o.email || o.user_id}</td>
                                <td>
                                    <DevForm.Select
                                        choices={Object.entries(GamePlaytestUserReqState).map(([k, v]) => ({
                                            value: k,
                                            label: v,
                                        }))}
                                        clearable={false}
                                        value={o.req_state}
                                        onChange={(data) => changeReqState(o.id, data.value)}
                                        menuPortalTarget={document.body} // решает проблему z-index с чойсами
                                    />
                                </td>
                                <td>{o.__access_type_get_display}</td>
                                <td>{o.date_added}</td>
                                <td>{o.date_access}</td>
                                <td>{o.comment}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </ListingCard>
    );
}

function PlaytestUsersManage({ gameId, header, act }) {
    const { t } = useTranslation();
    const { apiData } = useAppApi(url_reverse("api:game:playtest:users:manage", { game_id: gameId, act }));
    const onFormSubmit = useSubmit();

    if (!apiData) return;

    return (
        <CommonCard header={header}>
            <Form onSubmit={(ev) => onFormSubmit(ev, { backUrl: url_reverse("games:playtest", { game_id: gameId }) })}>
                <RenderForm bareForm={true} />
                <Row>
                    <Col md={3} className="d-flex align-items-end">
                        <DevForm.Button variant="primary" type="submit" name="action_form" value="main">
                            {t("Сохранить")}
                        </DevForm.Button>
                    </Col>
                    <Col>
                        {act === "users_add" && (
                            <i style={{ fontSize: "0.8rem" }}>
                                <Trans>
                                    Нажимая кнопку «Сохранить», вы поручаете ООО «ВК» обработку персональных данных в
                                    соответствии с{" "}
                                    <a href="https://documentation.vkplay.ru/terms_vkp/pdn_developer_agreement_vkp/">
                                        поручением на обработку персональных данных
                                    </a>
                                    , а также подтверждаете наличие предварительных согласий каждого адресата на
                                    обработку его персональных данных и на получение сообщений, связанных с
                                    использованием функционала «Тестирование игр».
                                </Trans>
                            </i>
                        )}
                    </Col>
                </Row>
            </Form>
        </CommonCard>
    );
}
